import React, { useCallback, useState, useRef } from 'react'
import type { Entry } from 'contentful'
import Link from '@/components/link-component'
import ImageContentful from './image-contentful'
import ImageZoomDialog from './image-zoom-dialog'
import { type TypeResourceImageSkeleton } from '@/types/ctf/TypeResourceImage'
import { cx } from '@/utils/strings'

export type ImageProps = Entry<TypeResourceImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  className?: string
  loading?: any
  invertSvgForDarkMode?: boolean
  zoomable?: boolean
  noOptimization?: boolean
}

export default function Image({
  invertSvgForDarkMode = false,
  className,
  loading,
  image,
  imageDarkMode,
  link,
  showCaption,
  noOptimization,
  zoomable,
}: ImageProps) {
  const fields = image?.fields || {}
  const [isZoomed, setIsZoomed] = useState(false)

  const triggerRef = useRef<HTMLDivElement>(null)

  const handleZoomToggle = useCallback(() => {
    if (window.innerWidth > 1024) {
      setIsZoomed(true)
    }
  }, [])

  const handleZoomClose = useCallback(() => {
    setIsZoomed(false)
  }, [])

  const Wrapper = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      if (link?.fields?.url)
        return (
          <Link title={`go to ${link.fields.url}`} to={link.fields.url}>
            {children}
          </Link>
        )
      return children
    },
    [link?.fields?.url]
  )

  return (
    <>
      <Wrapper>
        <figure className="mb-10 [&:not(:has(svg))]:w-fit print:[break-inside:avoid]">
          <div
            ref={triggerRef}
            className={cx(zoomable && 'lg:cursor-zoom-in')}
            onClick={zoomable ? handleZoomToggle : undefined}
          >
            <ImageContentful
              invertSvgForDarkMode={invertSvgForDarkMode}
              {...fields}
              darkModeImage={imageDarkMode?.fields}
              className={className}
              loading={loading}
              noOptimization={noOptimization}
            />
          </div>
          {showCaption && !isZoomed && (
            <figcaption className="w-fit max-w-sm mx-auto text-center text-xs text-secondary-600 dark:text-secondary-300 mt-2 print:mt-1">
              <cite>{image?.fields?.title}</cite>
              {image?.fields?.title && image?.fields?.description && <> &#8212; </>}
              {image?.fields?.description}
            </figcaption>
          )}
        </figure>
      </Wrapper>

      {zoomable && typeof window !== 'undefined' && window.innerWidth > 1024 && (
        <ImageZoomDialog
          isOpen={isZoomed}
          onClose={handleZoomClose}
          invertSvgForDarkMode={invertSvgForDarkMode}
          image={image?.fields}
          darkModeImage={imageDarkMode?.fields}
          noOptimization={noOptimization}
          showCaption={showCaption}
          triggerElement={triggerRef.current}
        />
      )}
    </>
  )
}
